import { useState } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { PayrollPeriodManager } from './PayrollPeriodManager';
import { PayrollCalculator } from './PayrollCalculator';
import { PayrollApprovalWorkflow } from './PayrollApprovalWorkflow';
import { PayrollPDFManager } from './PayrollPDFManager';
import { PayrollEmailManager } from './PayrollEmailManager';
import { Calculator, Calendar, CheckCircle, FileText, Mail } from 'lucide-react';

export function PayrollManagement() {
  const [selectedPeriodId, setSelectedPeriodId] = useState<string>('');

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Payroll Management</h1>
        <p className="text-muted-foreground">
          Manage Swiss payroll calculations, periods, and approvals
        </p>
      </div>

      <Tabs defaultValue="periods" className="space-y-6">
        <TabsList className="grid w-full grid-cols-5">
          <TabsTrigger value="periods" className="flex items-center gap-2">
            <Calendar className="h-4 w-4" />
            Payroll Periods
          </TabsTrigger>
          <TabsTrigger value="calculator" className="flex items-center gap-2">
            <Calculator className="h-4 w-4" />
            Calculator
          </TabsTrigger>
          <TabsTrigger value="approval" className="flex items-center gap-2">
            <CheckCircle className="h-4 w-4" />
            Approval
          </TabsTrigger>
          <TabsTrigger value="payslips" className="flex items-center gap-2">
            <FileText className="h-4 w-4" />
            Payslips
          </TabsTrigger>
          <TabsTrigger value="emails" className="flex items-center gap-2">
            <Mail className="h-4 w-4" />
            Emails
          </TabsTrigger>
        </TabsList>

        <TabsContent value="periods" className="space-y-6">
          <PayrollPeriodManager />
        </TabsContent>

        <TabsContent value="calculator" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Individual Payroll Calculator</CardTitle>
              <CardDescription>
                Calculate payroll for a specific employee with custom parameters
              </CardDescription>
            </CardHeader>
            <CardContent>
              <PayrollCalculator />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="approval" className="space-y-6">
          {selectedPeriodId ? (
            <PayrollApprovalWorkflow payrollPeriodId={selectedPeriodId} />
          ) : (
            <Card>
              <CardHeader>
                <CardTitle>Payroll Approval</CardTitle>
                <CardDescription>
                  Select a payroll period from the Periods tab to review and approve calculations
                </CardDescription>
              </CardHeader>
              <CardContent className="py-12">
                <div className="text-center text-muted-foreground">
                  <CheckCircle className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No payroll period selected for approval</p>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="emails" className="space-y-6">
          {selectedPeriodId ? (
            <PayrollEmailManager payrollPeriodId={selectedPeriodId} />
          ) : (
            <Card>
              <CardHeader>
                <CardTitle>Email Notifications</CardTitle>
                <CardDescription>
                  Select a payroll period from the Periods tab to manage email notifications
                </CardDescription>
              </CardHeader>
              <CardContent className="py-12">
                <div className="text-center text-muted-foreground">
                  <Mail className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No payroll period selected for email management</p>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="payslips" className="space-y-6">
          {selectedPeriodId ? (
            <PayrollPDFManager payrollPeriodId={selectedPeriodId} />
          ) : (
            <Card>
              <CardHeader>
                <CardTitle>Swiss Payslips</CardTitle>
                <CardDescription>
                  Select a payroll period from the Periods tab to generate Swiss-compliant payslips
                </CardDescription>
              </CardHeader>
              <CardContent className="py-12">
                <div className="text-center text-muted-foreground">
                  <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No payroll period selected for payslip generation</p>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}